export type PageLocation = { pathname: string; search: string }

/**
 * Whether this navigation is the query-string-only case that posthog-js drops.
 *
 * posthog-js owns every other case, and this must stay out of its way:
 *
 * | Navigation               | Captured by                            |
 * | ------------------------ | -------------------------------------- |
 * | First load               | posthog-js `_captureInitialPageview()` |
 * | Pathname change          | posthog-js `HistoryAutocapture`        |
 * | Query-string-only change | this rule                              |
 *
 * `HistoryAutocapture._capturePageview()` compares `location.pathname` alone (verified against
 * posthog-js@1.369.3), so `/app/settings?tab=project` → `?tab=billing` emits nothing. 19 files
 * in this app drive state through `nuqs` — settings tabs, improvements tabs, accuracy filters,
 * prompt detail — so without this those views are invisible in pageview analytics.
 *
 * Lives here rather than beside the component so it is testable in the `node` test environment
 * without dragging in React and `@posthog/next`.
 *
 * ⚠️ ON UPGRADING posthog-js: this supplements a library limitation, so re-check
 * `HistoryAutocapture._capturePageview()`. If it ever compares the full URL instead of just
 * `location.pathname`, this rule starts double-firing on every query change and should be
 * deleted along with `SearchParamsPageview`.
 */
export function isQueryOnlyNavigation(previous: PageLocation | null, next: PageLocation): boolean {
  // First run is the initial pageview, which posthog-js already captured.
  if (!previous) return false
  // A pathname change is HistoryAutocapture's job; capturing here too would double-fire.
  if (previous.pathname !== next.pathname) return false
  return previous.search !== next.search
}
